// Paste normalization for notes fields.
//
// Text copied out of PDFs (and many websites) carries a hard line break at
// every visual wrap point. Pasted as-is, those breaks end up printed on the
// pedigree. On paste we unwrap them to flowing text; line breaks the user
// types themselves are never touched.

/**
 * Unwrap hard-wrapped text: single line breaks become spaces, runs of blank
 * lines (deliberate paragraph gaps) are kept as one line break, and runs of
 * spaces/tabs collapse to a single space.
 */
export function unwrapPastedText(text) {
  const normalized = String(text == null ? '' : text).replace(/\r\n?/g, '\n');
  return normalized
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.replace(/\n/g, ' ').replace(/[ \t]+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
}

/**
 * onPaste handler for a controlled notes <textarea>/TextField.
 * If the clipboard text contains line breaks, cancels the default paste and
 * applies the unwrapped text at the caret (replacing any selection).
 *
 * @param {ClipboardEvent} event
 * @param {string} currentValue - current controlled value of the field
 * @param {(next: string) => void} applyValue - state setter for the field
 */
export function handleNotesPaste(event, currentValue, applyValue) {
  const pasted = event.clipboardData ? event.clipboardData.getData('text') : '';
  if (!pasted || !/[\r\n]/.test(pasted)) return; // nothing to clean up

  event.preventDefault();
  const cleaned = unwrapPastedText(pasted);
  const value = String(currentValue == null ? '' : currentValue);
  const el = event.target;
  const start = typeof el.selectionStart === 'number' ? el.selectionStart : value.length;
  const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : start;

  applyValue(value.slice(0, start) + cleaned + value.slice(end));

  // Restore the caret to just after the pasted text once React re-renders.
  const caret = start + cleaned.length;
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      try {
        el.setSelectionRange(caret, caret);
      } catch (err) { /* field may have unmounted */ }
    });
  }
}
